<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';

import TiktokLeadSettingsAPI from 'dashboard/api/tiktokLeadSettings.js';
import TiktokLeadSettingItem from './TiktokLeadSettingItem.vue';
import TiktokLeadSettingModal from './TiktokLeadSettingModal.vue';

const { t } = useI18n();
const settings = ref([]);
const isLoading = ref(false);
const showModal = ref(false);
const editingItem = ref(null);

const fetchSettings = async () => {
  isLoading.value = true;
  try {
    const { data } = await TiktokLeadSettingsAPI.get();
    settings.value = data;
  } catch {
    useAlert(t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.API.FETCH_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const openCreateModal = () => {
  editingItem.value = null;
  showModal.value = true;
};

const openEditModal = item => {
  editingItem.value = item;
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  editingItem.value = null;
};

const handleSave = async payload => {
  try {
    if (payload.id) {
      await TiktokLeadSettingsAPI.update(payload.id, payload.lead_setting);
      useAlert(t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.API.UPDATE_SUCCESS'));
    } else {
      await TiktokLeadSettingsAPI.create(payload.lead_setting);
      useAlert(t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.API.CREATE_SUCCESS'));
    }
    closeModal();
    fetchSettings();
  } catch {
    useAlert(t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.API.SAVE_ERROR'));
  }
};

const handleDelete = async item => {
  if (!window.confirm(t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.DELETE_CONFIRM'))) {
    return;
  }
  try {
    await TiktokLeadSettingsAPI.delete(item.id);
    useAlert(t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.API.DELETE_SUCCESS'));
    fetchSettings();
  } catch {
    useAlert(t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.API.DELETE_ERROR'));
  }
};

onMounted(fetchSettings);
</script>

<template>
  <div class="border-t border-n-weak p-6 bg-n-alpha-1 rounded-b-md">
    <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
      <div>
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.MAPPINGS_TITLE') }}
        </h3>
        <p class="text-xs text-n-slate-11 mt-0.5">
          {{ t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.MAPPINGS_DESCRIPTION') }}
        </p>
      </div>
      <Button
        sm
        blue
        :label="t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.ADD_NEW')"
        icon="i-lucide-plus"
        @click="openCreateModal"
      />
    </div>

    <div v-if="isLoading" class="text-center py-8 text-n-slate-11 text-sm">
      {{ t('INTEGRATION_SETTINGS.LOADING') }}...
    </div>

    <div v-else-if="settings.length === 0" class="text-center py-10 border border-dashed border-n-weak rounded-xl bg-n-alpha-2">
      <i class="i-lucide-file-text text-3xl text-n-slate-9 mb-2 block mx-auto" />
      <p class="text-sm font-medium text-n-slate-12">
        {{ t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.EMPTY_TITLE') }}
      </p>
      <p class="text-xs text-n-slate-11 mt-1 max-w-md mx-auto">
        {{ t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.EMPTY_DESCRIPTION') }}
      </p>
      <div class="mt-4">
        <Button
          sm
          faded
          blue
          :label="t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.ADD_NEW')"
          icon="i-lucide-plus"
          @click="openCreateModal"
        />
      </div>
    </div>

    <div v-else class="flex flex-col gap-3">
      <TiktokLeadSettingItem
        v-for="item in settings"
        :key="item.id"
        :setting="item"
        @edit="openEditModal"
        @delete="handleDelete"
      />
    </div>

    <TiktokLeadSettingModal
      :show="showModal"
      :setting-item="editingItem"
      @close="closeModal"
      @save="handleSave"
    />
  </div>
</template>
